import { useState, useEffect } from 'react';
import api from '../services/api';
import { Search, ClipboardList, Filter, User, Calendar, Tag, Info, AlertCircle, FileText } from 'lucide-react';

export default function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const [lRes, uRes] = await Promise.all([
        api.get(`/audit?${queryParams}`),
        api.get('/auth/users')
      ]);
      setLogs(lRes.data);
      setUsers(uRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(true);
      // Wait a bit for smooth transition
      setTimeout(() => setLoading(false), 300);
    }
  };

  useEffect(() => { loadData(); }, [filters]);

  const getActionColor = (action) => {
    if (action.includes('DELETE')) return 'text-rose-500 bg-rose-500/10 border-rose-500/20';
    if (action.includes('PRICE') || action.includes('RESET')) return 'text-amber-600 bg-amber-500/10 border-amber-500/20';
    if (action.includes('LOGIN')) return 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20';
    return 'text-indigo-600 bg-indigo-500/10 border-indigo-500/20';
  };

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <ClipboardList size={32} className="text-brand" /> System Audit Trail
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">A transparent, immutable log of all critical system actions.</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-theme-card p-6 rounded-[2rem] shadow-soft border border-theme grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Staff Member</label>
          <div className="relative">
            <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <select 
              value={filters.userId} 
              onChange={e => setFilters({...filters, userId: e.target.value})}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand appearance-none"
            >
              <option value="">All Users</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Action Type</label>
          <div className="relative">
            <Tag size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <select 
              value={filters.action} 
              onChange={e => setFilters({...filters, action: e.target.value})}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand appearance-none"
            >
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="PRICE_CHANGE">Price Change</option>
              <option value="STOCK_ADJUST">Stock Adjust</option>
              <option value="STAFF_CREATE">Staff Created</option>
              <option value="STAFF_UPDATE">Staff Updated</option>
              <option value="PASSWORD_RESET_ADMIN">Password Reset</option>
              <option value="PRODUCT_DELETE">Product Deleted</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Start Date</label>
          <div className="relative">
            <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <input 
              type="date" 
              value={filters.startDate}
              onChange={e => setFilters({...filters, startDate: e.target.value})}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">End Date</label>
          <div className="relative">
            <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
            <input 
              type="date" 
              value={filters.endDate}
              onChange={e => setFilters({...filters, endDate: e.target.value})}
              className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-10 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>
      </div>

      <div className="bg-theme-card rounded-[2.5rem] shadow-soft border border-theme overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-theme-main/10 border-b border-theme text-[10px] font-black uppercase tracking-widest text-theme-muted">
                <th className="px-8 py-5">Timestamp</th>
                <th className="px-6 py-5">User</th>
                <th className="px-6 py-5">Action</th>
                <th className="px-6 py-5">Entity</th>
                <th className="px-6 py-5">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-theme-main/5 transition-colors group">
                  <td className="px-8 py-5">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-theme-main">{new Date(log.createdAt).toLocaleDateString()}</span>
                      <span className="text-[10px] font-bold text-theme-muted uppercase">{new Date(log.createdAt).toLocaleTimeString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center font-black text-xs uppercase">
                        {log.user?.name?.[0] || 'S'}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-theme-main">{log.user?.name || 'System'}</span>
                        <span className="text-[10px] font-medium text-theme-muted">{log.user?.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${getActionColor(log.action)}`}>
                      {log.action.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-theme-muted" />
                      <span className="text-xs font-bold text-theme-main">{log.entity}</span>
                      <span className="text-[10px] font-black text-brand bg-brand/5 px-1.5 py-0.5 rounded uppercase">#{log.entityId}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="max-w-xs text-[10px] font-medium text-theme-muted line-clamp-2">
                      {log.details}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && !loading && (
            <div className="p-20 flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-theme-main/10 rounded-full flex items-center justify-center mb-6">
                <ClipboardList size={32} className="text-theme-muted opacity-30" />
              </div>
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight mb-1">No Records Found</h3>
              <p className="text-theme-muted text-sm font-medium">Try adjusting your filters to see more activity logs.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
